/** @jsx jsx */

import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useLayoutEffect,
  Fragment as _,
} from 'react';
import { Editor } from 'slate-react';
import { Value } from 'slate';
import { css, jsx } from '@emotion/core';
import EditMarker from 'components/UI/EditMarker';
import { connect, useSelector } from 'react-redux';

import renderMark from './renderMark';
import renderNode from './renderNode';
import renderInline from './renderInline';

import html from './serializer';
import slatePlainSerializer from 'slate-plain-serializer';
import getBlockById from 'services/apiHelpers/getBlockById';
import setBlock from 'services/apiHelpers/setBlockProps';
import setCurrentBlock from 'services/apiHelpers/setCurrentBlock';
import { Text } from '@chameleoncreator/creator-ui';
import getStore from 'services/getStore';
import { isKeyHotkey } from 'is-hotkey';
import ReadOnlyText from './ReadOnlyText';
import isIE from 'services/isIE';
import bulletStyles from './bulletStyles';

const store = getStore();

const isBoldHotkey = isKeyHotkey('mod+b');
const isItalicHotkey = isKeyHotkey('mod+i');
const isUnderlinedHotkey = isKeyHotkey('mod+u');

const schema = {
  marks: {
    score: {
      isVoid: true,
    },
  },
};

const getInitialValue = content =>
  Value.fromJSON(
    content || {
      document: {
        nodes: [
          {
            object: 'block',
            type: 'paragraph',
            nodes: [
              {
                object: 'text',
                // leaves: [
                //   {
                //     text: '',
                //   },
                // ],
              },
            ],
          },
        ],
      },
    }
  );

export const placeholderText = 'Add content...';

let delayUpdate;

const EditableText = ({ focusedNodeId, ...props }) => {
  const { id, className } = props;
  const block = getBlockById(id);
  const {
    content = '',
    json,
    borderRadius = 0,
    useBackgroundControls,
    backgroundColor,
    bulletColor = '#000',
  } = block.props;

  if (!window.dev()) {
    return (
      <ReadOnlyText
        content={content}
        {...{
          className,
          useBackgroundControls,
          borderRadius,
          backgroundColor,
          bulletColor,
        }}
      />
    );
  }

  const [state, setState] = useState({
    value: html.deserialize(content),
  });

  const text = useRef(null);
  const editor = useRef(null);
  const section = useRef(null);
  const selected = focusedNodeId === id;

  // const bullet = '"\\2022"';

  if (window.dev() && selected)
    window['__CURRENT_EDITOR_CLEAR_ALL_FORMATTING__'] = content => {
      setBlock(getBlockById(id), {
        content,
      });

      setState({
        value: html.deserialize(content),
      });
    };

  useEffect(() => {
    if (!selected) return;

    parent.editor = editor.current;
  }, [selected]);

  // not to
  // update with all the colors

  useLayoutEffect(() => {
    // test
    // if !window.dev() < ???
    if (!text.current) {
      return;
    }

    //
    const innerText = text.current.innerText
      .toString()
      .replace(/\s\s+/g, ' ')
      .trim();

    const hasPlaceholder = innerText === placeholderText;

    text.current.style.textAlign = '';

    if (hasPlaceholder)
      requestAnimationFrame(() => {
        if (text.current) text.current.style.textAlign = 'left';
      });
  });

  const onChange = ({ value }) => {
    // this was causing a loss of focus
    // assuming because of the update being delayed
    // if (!selected) {
    //   return;
    // }

    if (value.document !== state.value.document) {
      clearTimeout(delayUpdate);
      delayUpdate = setTimeout(() => {
        setBlock(getBlockById(id), {
          // text: slatePlainSerializer.serialize(value),
          content: html.serialize(value),
          // json: value.toJSON(),
        });
      }, 500);
    }

    setState({ value });
  };

  const onKeyDown = (e, editor, next) => {
    let mark;

    if (isBoldHotkey(e)) {
      mark = 'bold';
    } else if (isItalicHotkey(e)) {
      mark = 'italic';
    } else if (isUnderlinedHotkey(e)) {
      mark = 'underline';
    } else {
      return next();
    }

    e.preventDefault();
    editor.toggleMark(mark);
  };

  if (selected && editor.current) parent.editor = editor.current;

  return (
    <Text
      ref={text}
      id={`chameleon-text-${id}`}
      className={`chameleon-text${className ? ` ${className}` : ''}`}
      as="div"
      css={css`
			position: relative;
      width: 100%;
			padding: 12.5px 10px;
			${!selected && 'background: rgba(161, 188, 211, 0.15);'}
			box-shadow: ${selected ? '0 0 0 1px #ff0063' : 'none'};
		
      transition: background 0.1s, box-shadow 0.2s;

			&:hover {
				cursor: text;
				${!selected && 'box-shadow: 0 0 0 1px lightGrey;'}
				background: transparent;
      }
		
			* {
        pointer-events: ${selected ? 'auto' : 'none'};
      }

      ${bulletStyles(bulletColor)}
  
  `}
      style={
        useBackgroundControls || backgroundColor
          ? {
              borderRadius,
              background: backgroundColor,
              padding: `${12.5 + borderRadius}px ${10 + borderRadius}px`,
            }
          : {}
      }
      onClick={e => {
        e.stopPropagation();

        if (selected) return;
        setCurrentBlock({ id });
        setTimeout(() => {
          editor.current.focus();
        }, 10);
      }}
      fontFamily={store.getState().theme.fontFamily || ''}
    >
      <Editor
        key={id}
        id={id}
        ref={editor}
        value={state.value}
        placeholder={'Add content...'}
        // schema={schema}
        readOnly={!window.dev()}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onPaste={e => {
          let pasted = (
            e.clipboardData || (window as any).clipboardData
          ).getData('text');

          // break it
          const pastedToPs = pasted
            .split(/(\r\n|\n|\r)/g)
            .map(p => p.replace(/\s\s+/g, ' ').trim())
            .filter(p => p !== '')
            .map(p => `<p data-type="paragraph">${p}</p>`)
            .join('');

          // ' filter ' it ?
          const { document } = html.deserialize(pastedToPs);

          editor.current.insertFragment(document);
          // or just prang in the text ?
          // editor.current.insertText(paste);

          e.preventDefault();
        }}
        renderMark={renderMark}
        // renderNode
        renderBlock={renderNode}
        renderInline={renderInline}
      />

      {selected && parent.updateEditor && parent.updateEditor(Math.random())}

      {!selected && <EditMarker />}
    </Text>
  );
};

export default connect(
  ({ focusedNodeId }: { focusedNodeId: string | number }) => ({
    focusedNodeId,
    [Math.random()]: '',
  })
)(EditableText);
